import { useRef, useState } from 'react';
import { Turnstile } from '@marsidev/react-turnstile';
import {
  continueAnonymously,
  sendPasswordReset,
  signInWithEmail,
  updateRecoveredPassword,
} from '../../utils/authService';

export const AuthScreen = ({ mode, onModeChange }) => {
  const captchaRef = useRef(null);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [passwordConfirm, setPasswordConfirm] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [message, setMessage] = useState('');
  const [captchaToken, setCaptchaToken] = useState('');
  const captchaSiteKey = process.env.REACT_APP_TURNSTILE_SITE_KEY;

  const runAuthAction = async (action, successMessage, actionType) => {
    setBusy(true);
    setError('');
    setMessage('');
    try {
      await action();
      if (successMessage) {
        setMessage(successMessage);
      } else {
        window.location.replace('/');
      }
    } catch (authError) {
      console.error('Authentication action failed:', authError);
      if (/anonymous.*disabled/i.test(authError.message || '')) {
        setError('Guest sessions are disabled in this Supabase project. Enable anonymous sign-ins in Authentication settings, then try again.');
      } else if (/captcha_token|captcha.*invalid|captcha.*required/i.test(authError.message || '')) {
        setError('Turnstile verification failed. Please complete the challenge again and retry.');
      } else {
        setError(authError.message || 'The request could not be completed. Please try again.');
      }
    } finally {
      setBusy(false);
      if (actionType === 'guest') {
        setCaptchaToken('');
        captchaRef.current?.reset();
      }
    }
  };

  const handleSignIn = (event) => {
    event.preventDefault();
    runAuthAction(() => signInWithEmail(email, password));
  };

  const handleReset = (event) => {
    event.preventDefault();
    runAuthAction(
      () => sendPasswordReset(email),
      'If an account exists for that address, a password reset link is on its way.'
    );
  };

  const handleUpdatePassword = (event) => {
    event.preventDefault();
    setError('');
    if (password.length < 8) {
      setError('Use a password with at least 8 characters.');
      return;
    }
    if (password !== passwordConfirm) {
      setError('Passwords do not match.');
      return;
    }
    runAuthAction(() => updateRecoveredPassword(password));
  };

  const handleGuest = () => {
    if (!captchaToken) {
      setError('Complete the Turnstile check before continuing.');
      return;
    }
    runAuthAction(() => continueAnonymously(captchaToken), null, 'guest');
  };
  const isChecking = mode === 'checking';
  const isRecovery = mode === 'update_password';
  const isReset = mode === 'reset';
  const isSignIn = mode === 'sign_in';

  return (
    <main className="min-h-screen bg-black text-white flex items-center justify-center p-4">
      <section className="w-full max-w-md border-4 border-lime-500 bg-gradient-to-br from-gray-900 to-black p-6 sm:p-8">
        <h1 className="text-3xl sm:text-4xl font-black text-lime-400 uppercase tracking-tight">
          SENDITMAXX
        </h1>

        {isChecking ? (
          <p className="mt-6 text-gray-300 font-bold">Checking your account...</p>
        ) : mode === 'choose' ? (
          <>
            <h2 className="mt-6 text-xl font-black uppercase">Welcome back</h2>
            <p className="mt-2 text-gray-400 text-sm font-bold">
              Sign in to recover your progress and any SendIt Pass purchase linked to this account, or start anonymously.
            </p>
            <button
              type="button"
              onClick={() => onModeChange('sign_in')}
              className="w-full mt-6 py-3 bg-lime-500 hover:bg-lime-400 text-black font-black uppercase border-2 border-lime-400"
            >
              SIGN IN
            </button>
            <button
              type="button"
              onClick={handleGuest}
              disabled={busy || !captchaSiteKey || !captchaToken}
              className="w-full mt-3 py-3 bg-gray-900 hover:bg-gray-800 disabled:opacity-50 text-gray-200 font-black uppercase border-2 border-gray-600"
            >
              {busy ? 'STARTING...' : 'CONTINUE ANONYMOUSLY'}
            </button>
            {captchaSiteKey ? (
              <div className="mt-4 flex justify-center">
                <Turnstile
                  ref={captchaRef}
                  siteKey={captchaSiteKey}
                  onSuccess={(token) => { setCaptchaToken(token); setError(''); }}
                  onExpire={() => setCaptchaToken('')}
                  onError={() => {
                    setCaptchaToken('');
                    setError('Turnstile could not load. Check the public site key and allowed hostnames.');
                  }}
                />
              </div>
            ) : (
              <p role="alert" className="mt-4 text-red-400 text-sm font-bold">
                Anonymous sign-in is unavailable: configure REACT_APP_TURNSTILE_SITE_KEY with your Cloudflare Turnstile site key.
              </p>
            )}
            {error && <p role="alert" className="mt-4 text-red-400 text-sm font-bold">{error}</p>}
            {message && <p role="status" className="mt-4 text-lime-300 text-sm font-bold">{message}</p>}
            <p className="mt-3 text-gray-500 text-xs font-bold">
              Anonymous progress is tied to this browser until you link a login.
            </p>
          </>
        ) : (
          <>
            <h2 className="mt-6 text-xl font-black uppercase">
              {isRecovery ? 'Set a new password' : isReset ? 'Reset password' : 'Sign in'}
            </h2>
            <p className="mt-2 text-gray-400 text-sm font-bold">
              {isRecovery
                ? 'Choose a new password for your SendItMaxx account.'
                : isReset
                  ? 'Enter your account email and we will send a password reset link.'
                  : 'Sign in with the confirmed email on your account to restore progress and SendIt Pass access.'}
            </p>

            {isRecovery ? (
              <form onSubmit={handleUpdatePassword} className="mt-6 space-y-3">
                <input
                  type="password"
                  value={password}
                  onChange={(event) => setPassword(event.target.value)}
                  autoComplete="new-password"
                  minLength={8}
                  placeholder="New password (8+ characters)"
                  required
                  className="w-full bg-black border-2 border-lime-700 p-3 text-lime-300 font-bold placeholder-gray-600 focus:outline-none focus:border-lime-500"
                />
                <input
                  type="password"
                  value={passwordConfirm}
                  onChange={(event) => setPasswordConfirm(event.target.value)}
                  autoComplete="new-password"
                  minLength={8}
                  placeholder="Confirm new password"
                  required
                  className="w-full bg-black border-2 border-lime-700 p-3 text-lime-300 font-bold placeholder-gray-600 focus:outline-none focus:border-lime-500"
                />
                <button
                  type="submit"
                  disabled={busy || !password || !passwordConfirm}
                  className="w-full py-3 bg-lime-500 hover:bg-lime-400 disabled:opacity-50 text-black font-black uppercase border-2 border-lime-400"
                >
                  {busy ? 'UPDATING...' : 'UPDATE PASSWORD'}
                </button>
              </form>
            ) : (
              <form onSubmit={isReset ? handleReset : handleSignIn} className="mt-6 space-y-3">
                <input
                  type="email"
                  value={email}
                  onChange={(event) => setEmail(event.target.value)}
                  autoComplete="email"
                  placeholder="Email address"
                  maxLength={254}
                  required
                  className="w-full bg-black border-2 border-lime-700 p-3 text-lime-300 font-bold placeholder-gray-600 focus:outline-none focus:border-lime-500"
                />
                {isSignIn && (
                  <input
                    type="password"
                    value={password}
                    onChange={(event) => setPassword(event.target.value)}
                    autoComplete="current-password"
                    placeholder="Password"
                    required
                    className="w-full bg-black border-2 border-lime-700 p-3 text-lime-300 font-bold placeholder-gray-600 focus:outline-none focus:border-lime-500"
                  />
                )}
                <button
                  type="submit"
                  disabled={busy || !email || (isSignIn && !password)}
                  className="w-full py-3 bg-lime-500 hover:bg-lime-400 disabled:opacity-50 text-black font-black uppercase border-2 border-lime-400"
                >
                  {busy ? 'PLEASE WAIT...' : isReset ? 'SEND RESET LINK' : 'SIGN IN'}
                </button>
              </form>
            )}

            {error && <p role="alert" className="mt-4 text-red-400 text-sm font-bold">{error}</p>}
            {message && <p role="status" className="mt-4 text-lime-300 text-sm font-bold">{message}</p>}

            <div className="mt-4 flex flex-wrap justify-between gap-3 text-sm font-bold">
              {isSignIn && (
                <button
                  type="button"
                  onClick={() => { setError(''); setMessage(''); onModeChange('reset'); }}
                  className="text-lime-400 hover:text-lime-300"
                >
                  Forgot password?
                </button>
              )}
              <button
                type="button"
                onClick={() => {
                  setError('');
                  setMessage('');
                  onModeChange(isRecovery ? 'sign_in' : 'choose');
                }}
                className="text-gray-400 hover:text-white"
              >
                {isRecovery ? 'Back to sign in' : 'Back'}
              </button>
            </div>
          </>
        )}
      </section>
    </main>
  );
};
